import { useState, useEffect, useCallback } from 'react';
import { supabase } from '../lib/supabase';
import { mapDbCandidateToTalentProfile } from '../lib/mappers';
import type { TalentProfile } from '../types';

export type PortfolioScope = 'portfolio' | 'org';

// Columns needed for list view (no large blobs)
const LIST_SELECT = 'id, full_name, email, status, filename, content_hash, created_at, updated_at, availability_status, availability_type, availability_date, tjm_min, tjm_max, photo_url, assigned_to, created_by, organization_id, role_title, experience_years, domain_tags, top_skills';
// Full columns including summary_json — fetched only for the detail view of a single candidate
// scored_skills : niveaux 0-10 « lu & ancré » (P1.0ter), prioritaires sur les scores bruts du summary_json
const DETAIL_SELECT = `${LIST_SELECT}, summary_json, scored_skills`;

interface UseCandidatesResult {
  candidates: TalentProfile[];
  isLoading: boolean;
  error: string | null;
  refresh: () => Promise<void>;
  updateCandidate: (id: string, updates: Partial<TalentProfile>) => Promise<void>;
  fetchCandidateDetail: (id: string) => Promise<TalentProfile | null>;
}

export const useCandidates = (scope: PortfolioScope = 'org', userId?: string): UseCandidatesResult => {
  const [candidates, setCandidates] = useState<TalentProfile[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  const fetchCandidates = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);

      let query = supabase.from('candidates').select(LIST_SELECT);

      if (scope === 'portfolio' && userId) {
        query = query.or(`created_by.eq.${userId},assigned_to.eq.${userId}`);
      }

      const { data, error: dbError } = await query;
      if (dbError) throw dbError;
      if (data) setCandidates(data.map(mapDbCandidateToTalentProfile));
    } catch (err: any) {
      console.error('Error fetching candidates:', err);
      setError(err.message || 'Failed to fetch candidates');
    } finally {
      setIsLoading(false);
    }
  }, [scope, userId]);

  const fetchCandidateDetail = useCallback(async (id: string): Promise<TalentProfile | null> => {
    const { data, error: dbError } = await supabase
      .from('candidates')
      .select(DETAIL_SELECT)
      .eq('id', Number(id))
      .single();
    if (dbError || !data) return null;
    return mapDbCandidateToTalentProfile(data as any);
  }, []);

  const updateCandidate = useCallback(async (id: string, updates: Partial<TalentProfile>) => {
    try {
      const directColumns: Record<string, any> = { updated_at: new Date().toISOString() };
      let needsJsonUpdate = false;

      if ('availability_status' in updates) directColumns.availability_status = updates.availability_status ?? null;
      if ('availability_type' in updates) directColumns.availability_type = updates.availability_type ?? null;
      if ('availability_date' in updates) directColumns.availability_date = updates.availability_date ?? null;
      if ('tjm_min' in updates) directColumns.tjm_min = updates.tjm_min ?? null;
      if ('tjm_max' in updates) directColumns.tjm_max = updates.tjm_max ?? null;
      if ('photo_url' in updates) directColumns.photo_url = updates.photo_url ?? null;
      if (updates.contactInfo) needsJsonUpdate = true;

      if (needsJsonUpdate) {
        const { data: currentData, error: fetchError } = await supabase
          .from('candidates')
          .select('summary_json')
          .eq('id', Number(id))
          .single();

        if (fetchError) throw fetchError;
        if (!currentData) throw new Error('Candidate not found');

        const row = currentData as any;
        let currentJson = row.summary_json;
        if (typeof currentJson === 'string') {
          try { currentJson = JSON.parse(currentJson); } catch { currentJson = {}; }
        }
        currentJson = currentJson || {};
        const newJson = { ...currentJson };

        if (updates.contactInfo) {
          if (updates.contactInfo.phone) newJson.Contact_phone = updates.contactInfo.phone;
          if (updates.contactInfo.email) newJson.Contact_email = updates.contactInfo.email;
          if (updates.contactInfo.linkedin_url) newJson.Contact_linkedin = updates.contactInfo.linkedin_url;
          newJson.contactInfo = { ...(newJson.contactInfo || {}), ...updates.contactInfo };

          // Le moteur de matching lit `summary_json.location`, jamais le bloc contactInfo
          // (cf. matching-init-rec). Sans cette recopie, une correction de ville resterait
          // invisible du scoring, donc la pénalité géographique serait inattaquable.
          const newCity = updates.contactInfo.location_city;
          if (newCity !== undefined && newCity !== newJson.location) {
            newJson.location = newCity;
            // Les coordonnées portent l'ANCIENNE ville et priment sur le libellé dans
            // `candidateInputFromRec` (`geo ?? location`) : les garder écraserait la
            // correction. On les retire — `backfill-geo-rec` re-géocodera, et d'ici là la
            // comparaison retombe sur le libellé, qui est désormais le bon.
            delete newJson.location_geo;
          }
        }

        directColumns.summary_json = newJson;
      }

      const { error: updateError } = await supabase
        .from('candidates')
        .update(directColumns)
        .eq('id', Number(id));

      if (updateError) throw updateError;
      await fetchCandidates();
    } catch (err: any) {
      console.error('Error updating candidate:', err);
      throw err;
    }
  }, [fetchCandidates]);

  useEffect(() => {
    fetchCandidates();
  }, [fetchCandidates]);

  return { candidates, isLoading, error, refresh: fetchCandidates, updateCandidate, fetchCandidateDetail };
};
